import rg4js from "raygun4js";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSessionStorage } from "usehooks-ts";
import type { CreateOfficeModel, Preferences } from "@/briefpoint-client";
import {
  ContactInfoCategory,
  ContactInfoType,
  ExternalPartners,
} from "@/briefpoint-client";
import { useFirmApi, useJurisdictionApi, useUserApi } from "@/hooks/useApi";
import useMixpanel from "@/hooks/useMixpanel";
import { CaliforniaId } from "@/lib/constants";
import type {
  FirmClient,
  FirmResponse,
  JurisdictionResponse,
  OfficeResponse,
  UserResponse,
} from "@/lib/types";
import { transformFirm } from "@/lib/utils";
import { promiseRetry } from "@/utils";

interface AuthProvider {
  user: UserResponse | undefined;
  setUser: React.Dispatch<React.SetStateAction<UserResponse | undefined>>;
  getUser: () => Promise<UserResponse | undefined>;
  getFirm: (user: UserResponse) => Promise<FirmClient | undefined>;
  createUser: (
    useDefaultJurisdiction: boolean
  ) => Promise<UserResponse | undefined>;
  updateUser: (user: UserResponse) => Promise<void>;
  firm: FirmClient | undefined;
  setFirm: React.Dispatch<React.SetStateAction<FirmClient | undefined>>;
  updateFirm: (firm: FirmClient) => Promise<void>;
  updatePreferences: (preferences: Preferences) => Promise<void>;
  firmAddOffice: (
    firm: FirmClient,
    createOfficeModel: CreateOfficeModel
  ) => Promise<void>;
  baseUser: UserResponse | undefined;
  jurisdictions: JurisdictionResponse[] | undefined; //TODO: this doesn't technically belong in "AuthProvider" but I couldn't figure out a better way to have this value cached for the life of the app
  defaultJurisdiction: JurisdictionResponse;
}

export const OnBehalfOfSessionKey = "on_behalf_of";
export const OnBehalfOfHeaderKey = "SA-ONBEHALF-OF";

const authContext = createContext<AuthProvider>({
  user: undefined,
  setUser: () => {},
  getUser: async () => undefined,
  getFirm: async () => undefined,
  createUser: async () => undefined,
  updateUser: async () => {},
  firm: undefined,
  setFirm: () => {},
  updateFirm: async () => {},
  updatePreferences: async () => {},
  firmAddOffice: async () => {},
  baseUser: undefined,
  jurisdictions: undefined,
  defaultJurisdiction: {} as JurisdictionResponse,
});

// Provider component that wraps your app and makes auth object
// available to any child component that calls useAuth().
export function ProvideAuth({ children }: { children: ReactNode }) {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

// Hook for child components to get the auth object and re-render when it changes.
export const useAuth = () => {
  return useContext(authContext);
};

/**
 * TODO: destroy this. It's misnamed as it doesn't have anything to do with auth,
 * and it creates a race condition with the ApiProvider. Instead, components
 * should use react-query to get their own data. When you do, you can remove
 * AuthWrapper from main.tsx.
 */
// Provider hook that creates auth object and handles state
function useProvideAuth() {
  const [user, setUser] = useState<UserResponse>();
  const [baseUser, setBaseUser] = useState<UserResponse>();
  const [firm, setFirm] = useState<FirmClient>();
  const [onbehalfof] = useSessionStorage(OnBehalfOfSessionKey, undefined);
  const alreadyLoading = useRef<string | undefined>(undefined);

  const userApi = useUserApi();
  const firmApi = useFirmApi();
  const jurisdictionApi = useJurisdictionApi();

  const [, mixpanelIdentify] = useMixpanel();
  const [jurisdictions, setJurisdictions] = useState<JurisdictionResponse[]>();
  const jurisdictionsCall = useRef<Promise<JurisdictionResponse[]> | undefined>(
    undefined
  );
  const jurisdictionId = firm?.offices
    ?.at(0)
    ?.jurisdictions?.at(0)?.jurisdictionId;
  const defaultJurisdiction =
    (jurisdictions ?? []).find((jx) => jx.id === jurisdictionId) ??
    (jurisdictions ?? []).find((jx) => jx.id === CaliforniaId) ??
    (jurisdictions ?? [])[0];

  const getFirm = useCallback(
    async (user: UserResponse) => {
      if (user.firmId) {
        const firm = (await firmApi.firmGet({
          id: user.firmId,
        })) as FirmResponse;
        const transformedFirmData = transformFirm(firm);
        setFirm(transformedFirmData);
        return transformedFirmData;
      }
    },
    [firmApi]
  );

  const setRaygunUser = useCallback(
    async (user: UserResponse) => {
      const userEmails = user.contactInfo?.filter(
        (c) => c.type === ContactInfoType.Email
      );
      // find office email, if not found, use first email
      const officeEmail =
        userEmails?.find((c) => c.category === ContactInfoCategory.Office) ||
        userEmails?.[0];

      mixpanelIdentify(user.id);
      rg4js("setUser", {
        identifier: user.id,
        isAnonymous: false,
        firstName: user?.firstName || "",
        fullName: `${user?.firstName || ""} ${user?.lastName || ""}`,
        email: officeEmail?.value || "",
      });
    },
    [mixpanelIdentify]
  );

  const handleGetUser = useCallback(async () => {
    const user = (await userApi.userGet()) as UserResponse;
    if (user) {
      setUser(user);
      setBaseUser(user);
      setRaygunUser(user);
    }
    return user;
  }, [userApi, setRaygunUser]);

  const handleCreateUser = useCallback(
    async (createDefaultJurisdiction: boolean) => {
      let partner: ExternalPartners | undefined;
      const externalPartnerQuery = sessionStorage.getItem("externalPartner");
      if (externalPartnerQuery) {
        const val = externalPartnerQuery as ExternalPartners;
        if (val && val in ExternalPartners) {
          partner = val;
        }
      }
      const user = (await userApi.userPost({
        useDefaultJurisdiction: createDefaultJurisdiction,
        partner,
      })) as UserResponse;
      if (user) {
        setUser(user);
        setBaseUser(user);
        setRaygunUser(user);
        await getFirm(user);
      }
      return user;
    },
    [getFirm, userApi, setRaygunUser]
  );

  const handleUpdateUser = useCallback(
    async (user: UserResponse) => {
      if (user) {
        const updatedUser = (await userApi.userPut({ user })) as UserResponse;
        setUser(updatedUser);
        setRaygunUser(user);
      }
    },
    [userApi, setRaygunUser]
  );

  const handleUpdateFirm = useCallback(
    async (firm: FirmClient) => {
      if (firm) {
        const updatedFirm = (await firmApi.firmPut({
          id: firm.id,
          firm,
        })) as FirmResponse;
        setFirm(transformFirm(updatedFirm));
      }
    },
    [firmApi]
  );

  const handleFirmAddOffice = useCallback(
    async (firm: FirmResponse, createOfficeModel: CreateOfficeModel) => {
      const office = (await firmApi.firmCreateOffice({
        id: firm.id,
        createOfficeModel,
      })) as OfficeResponse;
      setFirm(
        transformFirm({ ...firm, offices: [...(firm.offices || []), office] })
      );
    },
    [firmApi]
  );

  const handleUpdatePreferences = useCallback(
    async (p: Preferences) => {
      if (p) {
        await userApi.userUpdatePreferences({ preferences: p });
        setUser((u) => ({ ...u, preferences: p }) as UserResponse);
      }
    },
    [userApi]
  );

  useEffect(() => {
    const loadFirm = async (user: UserResponse) => {
      await getFirm(user);
    };
    if (
      user?.firmId &&
      user.firmId !== firm?.id &&
      (!onbehalfof || onbehalfof === user.id)
    ) {
      loadFirm(user);
    }
  }, [user, firm, getFirm, onbehalfof]);

  useEffect(() => {
    const loadUser = async (userId: string) => {
      const onbehalfof_user = (await userApi.userGetUser({
        userId,
      })) as UserResponse;
      if (onbehalfof_user) {
        setUser(onbehalfof_user);
      }
    };
    if (onbehalfof) {
      if (
        user &&
        user.id !== onbehalfof &&
        alreadyLoading?.current !== onbehalfof
      ) {
        alreadyLoading.current = onbehalfof;
        loadUser(onbehalfof);
      }
    } else if (user && baseUser && baseUser?.id !== user?.id) {
      alreadyLoading.current = undefined;
      setUser(baseUser);
    }
  }, [onbehalfof, baseUser, user, userApi]);

  useEffect(() => {
    const loadJxs = async () => {
      if (!jurisdictionsCall.current) {
        jurisdictionsCall.current = promiseRetry(
          () =>
            jurisdictionApi.jurisdictionGet() as Promise<
              JurisdictionResponse[]
            >,
          500,
          5
        );
      }

      const jxs = await jurisdictionsCall.current;
      setJurisdictions((x) => {
        if (!x) return jxs;

        return x;
      });
    };

    if (user && !jurisdictions) {
      loadJxs();
    }
  }, [jurisdictionApi, jurisdictions, user]);

  return {
    user,
    setUser,
    getUser: handleGetUser,
    getFirm,
    createUser: handleCreateUser,
    updateUser: handleUpdateUser,
    firm,
    setFirm,
    updateFirm: handleUpdateFirm,
    updatePreferences: handleUpdatePreferences,
    firmAddOffice: handleFirmAddOffice,
    baseUser,
    jurisdictions,
    defaultJurisdiction,
  };
}
